import { useMemo } from "react";
import "./Calendar.css";
import CalendarItem from "./CalendarItem";

const fullDayHeaders = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

const weekdayHeaders = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];

function formatDateKey(date) {
  return date.toLocaleDateString("sv-SE");
}

function generateMonthGrid(year, month, showWeekends = true) {
  const days = [];

  const totalDays = new Date(year, month + 1, 0).getDate();
  const dates = [];

  for (let day = 1; day <= totalDays; day++) {
    const date = new Date(year, month, day);
    const jsDay = date.getDay();
    if (!showWeekends && (jsDay === 0 || jsDay === 6)) continue;
    dates.push(date);
  }

  const firstVisibleDay = dates[0];
  const firstVisibleJsDay = firstVisibleDay.getDay();

  const cols = showWeekends ? 7 : 5;

  // Calculate how many empty cells to insert before the first visible date
  let emptyCells = showWeekends
    ? (firstVisibleJsDay + 6) % 7
    : (() => {
        switch (firstVisibleJsDay) {
          case 1:
            return 0;
          case 2:
            return 1;
          case 3:
            return 2;
          case 4:
            return 3;
          case 5:
            return 4;
          default:
            return 5;
        }
      })();

  for (let i = 0; i < emptyCells; i++) {
    days.push(null);
  }

  for (const date of dates) {
    days.push(date);
  }

  while (days.length % cols !== 0) {
    days.push(null);
  }

  return days;
}

export default function MonthlyGrid({
  date = new Date(),
  showWeekends = true,
  onSelectDay,
  data = [],
}) {
  const year = date.getFullYear();
  const month = date.getMonth();

  const dayHeaders = showWeekends ? fullDayHeaders : weekdayHeaders;

  const flatDays = generateMonthGrid(year, month, showWeekends);

  const dataByDate = useMemo(() => {
    const map = new Map();

    if (!Array.isArray(data)) return map;

    data.forEach((entry) => {
      const key = formatDateKey(new Date(entry.scheduleDate));
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(entry);
    });

    for (const [key, entries] of map) {
      entries.sort((a, b) => {
        const [aH, aM] = a.startTime ? a.startTime.split(":").map(Number) : [0, 0];
        const [bH, bM] = b.startTime ? b.startTime.split(":").map(Number) : [0, 0];
        return aH * 60 + aM - (bH * 60 + bM);
      });
    }

    return map;
  }, [data]);

  const onDayClick = (day) => {
    if (day && day.getMonth() === month && onSelectDay) {
      onSelectDay(day);
    }
  };

  return (
    <div className="monthly-grid">
      <div
        className="monthly-grid-header"
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(${dayHeaders.length}, 1fr)`,
        }}
      >
        {dayHeaders.map((day) => (
          <div key={day} className="monthly-grid-header-cell">
            {day}
          </div>
        ))}
      </div>

      <div
        className="monthly-grid-body"
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(${dayHeaders.length}, 1fr)`,
        }}
      >
        {flatDays.map((day, idx) => {
          const isToday =
            day &&
            day.getDate() === new Date().getDate() &&
            day.getMonth() === new Date().getMonth() &&
            day.getFullYear() === new Date().getFullYear();

          const isCurrentMonth = day && day.getMonth() === month;
          const dayKey = day ? formatDateKey(day) : "";
          const dayEntries = day ? dataByDate.get(dayKey) || [] : [];

          return (
            <div
              key={idx}
              className={`monthly-grid-cell ${
                !day || !isCurrentMonth ? "monthly-grid-cell-placeholder" : ""
              }`}
              onClick={() => onDayClick(day)}
            >
              {day && (
                <>
                  <div
                    className={`monthly-grid-cell-date ${
                      isToday ? "monthly-grid-cell-date-today" : ""
                    }`}
                  >
                    {day.getDate()}
                  </div>

                  <div className="monthly-grid-cell-entries">
                    {dayEntries.slice(0, 2).map((entry, idx) => (
                      <CalendarItem
                        key={entry._id}
                        data={entry}
                        compact={true}
                        remainingCount={
                          idx === 1 && dayEntries.length > 2
                            ? dayEntries.length - 2
                            : 0
                        }
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
